import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';

type Props = {
  children: ReactNode;
};

type DisplayType = 'list' | 'kanban';

export type PurchaseOrderContractTaskSearchCondition = {
  identifier: string;
  page: number;
  per?: number;
  purchase_order_contract_task_phase_ids: number[];
  user_ids: number[];
  business_partner_ids: number[];
  team_ids: number[];
  subscribed: boolean;
  purchase_order_id?: number;
  archived: boolean;
  enabled: boolean;
};
export const defaultPurchaseOrderContractTaskSearchCondition: PurchaseOrderContractTaskSearchCondition = {
  identifier: '',
  page: 1,
  purchase_order_contract_task_phase_ids: [],
  user_ids: [],
  business_partner_ids: [],
  team_ids: [],
  subscribed: false,
  archived: false,
  enabled: false,
};

export type PaymentTaskSearchCondition = {
  identifier: string;
  page: number;
  per?: number;
  billing_year_months: string[];
  payment_task_phase_ids: number[];
  user_ids: number[];
  business_partner_ids: number[];
  team_ids: number[];
  payment_method_ids: number[];
  subscribed: boolean;
  purchase_order_id?: number;
  archived: boolean;
  enabled: boolean;
};
export const defaultPaymentTaskSearchCondition: PaymentTaskSearchCondition = {
  identifier: '',
  page: 1,
  billing_year_months: [],
  payment_task_phase_ids: [],
  user_ids: [],
  business_partner_ids: [],
  team_ids: [],
  payment_method_ids: [],
  subscribed: false,
  archived: false,
  enabled: false,
};

export type SalesOrderContractTaskSearchCondition = {
  identifier: string;
  page: number;
  per?: number;
  sales_order_contract_task_phase_ids: number[];
  user_ids: number[];
  business_partner_ids: number[];
  agency_ids: number[];
  team_ids: number[];
  subscribed: boolean;
  sales_order_id?: number;
  archived: boolean;
  enabled: boolean;
};
export const defaultSalesOrderContractTaskSearchCondition: SalesOrderContractTaskSearchCondition = {
  identifier: '',
  page: 1,
  sales_order_contract_task_phase_ids: [],
  user_ids: [],
  business_partner_ids: [],
  agency_ids: [],
  team_ids: [],
  subscribed: false,
  archived: false,
  enabled: false,
};

export type QuotationTaskSearchCondition = {
  identifier: string;
  page: number;
  per?: number;
  quotation_task_phase_ids: number[];
  user_ids: number[];
  business_partner_ids: number[];
  agency_ids: number[];
  team_ids: number[];
  subscribed: boolean;
  sales_order_id?: number;
  archived: boolean;
  enabled: boolean;
};
export const defaultQuotationTaskSearchCondition: QuotationTaskSearchCondition = {
  identifier: '',
  page: 1,
  quotation_task_phase_ids: [],
  user_ids: [],
  business_partner_ids: [],
  agency_ids: [],
  team_ids: [],
  subscribed: false,
  archived: false,
  enabled: false,
};

export type InvoiceTaskSearchCondition = {
  identifier: string;
  page: number;
  per?: number;
  billing_year_months: string[];
  invoice_task_phase_ids: number[];
  user_ids: number[];
  business_partner_ids: number[];
  agency_ids: number[];
  team_ids: number[];
  product_ids: number[];
  subscribed: boolean;
  sales_order_id?: number;
  archived: boolean;
  enabled: boolean;
};
export const defaultInvoiceTaskSearchCondition: InvoiceTaskSearchCondition = {
  identifier: '',
  page: 1,
  billing_year_months: [],
  invoice_task_phase_ids: [],
  user_ids: [],
  business_partner_ids: [],
  agency_ids: [],
  team_ids: [],
  product_ids: [],
  subscribed: false,
  archived: false,
  enabled: false,
};

export type BackofficeSearchConditionType = {
  purchaseOrderContractTaskDisplayType: DisplayType;
  setPurchaseOrderContractTaskDisplayType: Dispatch<SetStateAction<DisplayType>>;
  purchaseOrderContractTaskSearchCondition: PurchaseOrderContractTaskSearchCondition;
  setPurchaseOrderContractTaskSearchCondition: Dispatch<SetStateAction<PurchaseOrderContractTaskSearchCondition>>;

  paymentTaskDisplayType: DisplayType;
  setPaymentTaskDisplayType: Dispatch<SetStateAction<DisplayType>>;
  paymentTaskSearchCondition: PaymentTaskSearchCondition;
  setPaymentTaskSearchCondition: Dispatch<SetStateAction<PaymentTaskSearchCondition>>;

  salesOrderContractTaskDisplayType: DisplayType;
  setSalesOrderContractTaskDisplayType: Dispatch<SetStateAction<DisplayType>>;
  salesOrderContractTaskSearchCondition: SalesOrderContractTaskSearchCondition;
  setSalesOrderContractTaskSearchCondition: Dispatch<SetStateAction<SalesOrderContractTaskSearchCondition>>;

  quotationTaskDisplayType: DisplayType;
  setQuotationTaskDisplayType: Dispatch<SetStateAction<DisplayType>>;
  quotationTaskSearchCondition: QuotationTaskSearchCondition;
  setQuotationTaskSearchCondition: Dispatch<SetStateAction<QuotationTaskSearchCondition>>;

  invoiceTaskDisplayType: DisplayType;
  setInvoiceTaskDisplayType: Dispatch<SetStateAction<DisplayType>>;
  invoiceTaskSearchCondition: InvoiceTaskSearchCondition;
  setInvoiceTaskSearchCondition: Dispatch<SetStateAction<InvoiceTaskSearchCondition>>;
};

export const BackofficeSearchConditionContext = createContext<BackofficeSearchConditionType>({
  purchaseOrderContractTaskDisplayType: 'list',
  setPurchaseOrderContractTaskDisplayType: () => {},
  purchaseOrderContractTaskSearchCondition: defaultPurchaseOrderContractTaskSearchCondition,
  setPurchaseOrderContractTaskSearchCondition: () => {},

  paymentTaskDisplayType: 'list',
  setPaymentTaskDisplayType: () => {},
  paymentTaskSearchCondition: defaultPaymentTaskSearchCondition,
  setPaymentTaskSearchCondition: () => {},

  salesOrderContractTaskDisplayType: 'list',
  setSalesOrderContractTaskDisplayType: () => {},
  salesOrderContractTaskSearchCondition: defaultSalesOrderContractTaskSearchCondition,
  setSalesOrderContractTaskSearchCondition: () => {},

  quotationTaskDisplayType: 'list',
  setQuotationTaskDisplayType: () => {},
  quotationTaskSearchCondition: defaultQuotationTaskSearchCondition,
  setQuotationTaskSearchCondition: () => {},

  invoiceTaskDisplayType: 'list',
  setInvoiceTaskDisplayType: () => {},
  invoiceTaskSearchCondition: defaultInvoiceTaskSearchCondition,
  setInvoiceTaskSearchCondition: () => {},
});

export const BackofficeSearchConditionProvider = ({ children }: Props) => {
  const { _w_ } = useCurrentWorkspace();

  const [purchaseOrderContractTaskDisplayType, setPurchaseOrderContractTaskDisplayType] = useState<DisplayType>('list');
  const [purchaseOrderContractTaskSearchCondition, setPurchaseOrderContractTaskSearchCondition] =
    useState<PurchaseOrderContractTaskSearchCondition>(defaultPurchaseOrderContractTaskSearchCondition);

  const [paymentTaskDisplayType, setPaymentTaskDisplayType] = useState<DisplayType>('list');
  const [paymentTaskSearchCondition, setPaymentTaskSearchCondition] = useState<PaymentTaskSearchCondition>(
    defaultPaymentTaskSearchCondition
  );

  const [salesOrderContractTaskDisplayType, setSalesOrderContractTaskDisplayType] = useState<DisplayType>('list');
  const [salesOrderContractTaskSearchCondition, setSalesOrderContractTaskSearchCondition] =
    useState<SalesOrderContractTaskSearchCondition>(defaultSalesOrderContractTaskSearchCondition);

  const [quotationTaskDisplayType, setQuotationTaskDisplayType] = useState<DisplayType>('list');
  const [quotationTaskSearchCondition, setQuotationTaskSearchCondition] = useState<QuotationTaskSearchCondition>(
    defaultQuotationTaskSearchCondition
  );

  const [invoiceTaskDisplayType, setInvoiceTaskDisplayType] = useState<DisplayType>('list');
  const [invoiceTaskSearchCondition, setInvoiceTaskSearchCondition] = useState<InvoiceTaskSearchCondition>(
    defaultInvoiceTaskSearchCondition
  );

  useEffect(() => {
    if (_w_) {
      setPurchaseOrderContractTaskSearchCondition((c) => ({ ...c, identifier: _w_!.identifier }));
      setPaymentTaskSearchCondition((c) => ({ ...c, identifier: _w_!.identifier }));
      setSalesOrderContractTaskSearchCondition((c) => ({ ...c, identifier: _w_!.identifier }));
      setQuotationTaskSearchCondition((c) => ({ ...c, identifier: _w_!.identifier }));
      setInvoiceTaskSearchCondition((c) => ({ ...c, identifier: _w_!.identifier }));
    }
  }, [_w_]);

  return (
    <BackofficeSearchConditionContext.Provider
      value={{
        purchaseOrderContractTaskDisplayType,
        setPurchaseOrderContractTaskDisplayType,
        purchaseOrderContractTaskSearchCondition,
        setPurchaseOrderContractTaskSearchCondition,

        paymentTaskDisplayType,
        setPaymentTaskDisplayType,
        paymentTaskSearchCondition,
        setPaymentTaskSearchCondition,

        salesOrderContractTaskDisplayType,
        setSalesOrderContractTaskDisplayType,
        salesOrderContractTaskSearchCondition,
        setSalesOrderContractTaskSearchCondition,

        quotationTaskDisplayType,
        setQuotationTaskDisplayType,
        quotationTaskSearchCondition,
        setQuotationTaskSearchCondition,

        invoiceTaskDisplayType,
        setInvoiceTaskDisplayType,
        invoiceTaskSearchCondition,
        setInvoiceTaskSearchCondition,
      }}
    >
      {children}
    </BackofficeSearchConditionContext.Provider>
  );
};

export const useBackofficeSearchCondition = () => {
  return useContext(BackofficeSearchConditionContext);
};
